/* eslint-disable react/forbid-prop-types */
import React from 'react';
import { shape, string, bool, object } from 'prop-types';
import commonOptionsPropType from 'TMPropTypes/commonOptionsProps';
import styled from 'styled-components';
import imageProps from 'TMPropTypes/imageProps';

import ContentParserStructured from 'TMComponents/ContentParserStructured';
import Img from 'TMComponents/ImageDynamic';
import {
    H1,
    H2,
    H3,
    H4,
    H5,
    H6,
    BodyReg,
    BodyRegBold,
    BodySm,
    BodySmBold,
    BodyXs,
    BodyXsBold
} from 'TMComponents/Typography';
import { Button } from 'TMComponents/Button';
import stringToDast from 'TMUtils/stringToDast';
import isStructuredContentEmpty from 'TMUtils/isStructuredContentEmpty';
import { breakpoints } from 'TMConstants';

import { HStack } from '../../designSystem/layouts';
import { spacing } from '../../designSystem/spacing';

// Helper functions
const getBodyCopyVariant = (size) => {
    const variants = {
        xs: 'extra_small',
        small: 'small',
        regular: 'default'
    };
    return variants[size] || variants.regular;
};

const isHorizontalLayout = (
    iconSize,
    regularIconHorizontalMobileLayout
) => {
    return iconSize === 'small' || regularIconHorizontalMobileLayout;
};

const shouldCenterAlignText = (
    iconSize,
    regularIconHorizontalMobileLayout
) => {
    return iconSize !== 'small' && !regularIconHorizontalMobileLayout;
};

// Icon size constants
const ICON_SIZES = {
    small: '40px',
    regular: '80px',
    large: '120px'
};

const Container = styled.div`
    margin-bottom: 20px;
    width: 100%;
    max-width: 768px;
    margin-left: auto;
    margin-right: auto;

    @media (min-width: ${breakpoints.mobileTablet + 1}px) {
        padding: 0 ${spacing.medium};
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const BaseContentSection = styled(HStack)`
    margin-bottom: ${(props) => (props.hasButton ? spacing.large : '0')};
    width: 100%;
    box-sizing: border-box;
`;

const HorizontalContentSection = styled(BaseContentSection)`
    @media (max-width: ${breakpoints.mobileTablet}px) {
        flex-direction: row;
        align-items: ${(props) =>
            props.iconSize === 'small' && props.iconPosition === 'center'
                ? 'center'
                : 'flex-start'};
        text-align: left;
        gap: ${spacing.small};
    }
`;

const VerticalContentSection = styled(BaseContentSection)`
    @media (max-width: ${breakpoints.mobileTablet}px) {
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: ${spacing.medium};
    }
`;

const IconWrapper = styled.div`
    flex-shrink: 0;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: ${(props) =>
        props.iconSize === 'small' ? spacing.tiny : '0'};
    width: ${(props) => ICON_SIZES[props.iconSize] || ICON_SIZES.regular};
    min-width: ${(props) =>
        ICON_SIZES[props.iconSize] || ICON_SIZES.regular};
    height: ${(props) => ICON_SIZES[props.iconSize] || ICON_SIZES.regular};

    @media (min-width: ${breakpoints.mobileTablet + 1}px) {
        margin-top: 0;
    }

    * {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
    }

    img,
    picture,
    .gatsby-image-wrapper {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
    }
`;

const ContentWrapper = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
    color: ${(props) =>
        props.textColor === 'light' ? 'white' : 'inherit'};
    word-wrap: break-word;
    overflow-wrap: break-word;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin-top: 0;
        /* istanbul ignore next */
        margin-bottom: ${(props) => {
            if (
                props.iconPosition === 'center' &&
                !props.hasContentAfterTitle
            ) {
                /* istanbul ignore next */
                return '0';
            }
            return props.hasContentAfterTitle ? '16px' : '8px';
        }};
        line-height: 1.2;
        text-align: inherit;
    }

    p {
        margin-top: 0;
        margin-bottom: 0;
        text-align: inherit;
    }

    @media (max-width: ${breakpoints.mobileTablet}px) {
        margin-top: 0;
        justify-content: flex-start;
        width: 100%;
        box-sizing: border-box;
        /* istanbul ignore next */
        text-align: ${(props) =>
            isHorizontalLayout(
                props.iconSize,
                props.regularIconHorizontalMobileLayout
            )
                ? 'left'
                : 'center'};
        /* istanbul ignore next */
        max-width: ${(props) =>
            props.iconSize === 'regular' &&
            !props.regularIconHorizontalMobileLayout
                ? '360px'
                : '100%'};
    }
`;

const ButtonSection = styled.div`
    display: flex;
    justify-content: center;
`;

const ClickableLink = styled.a`
    text-decoration: none;
    color: inherit;
    display: contents;
    cursor: pointer;
`;

// Component maps
const HEADING_COMPONENTS = {
    h1: H1,
    h2: H2,
    h3: H3,
    h4: H4,
    h5: H5,
    h6: H6
};

const BODY_COMPONENTS = {
    regular: { normal: BodyReg, bold: BodyRegBold },
    small: { normal: BodySm, bold: BodySmBold },
    xs: { normal: BodyXs, bold: BodyXsBold }
};

// Helper components
const DynamicHeading = ({ titleSize, variant, children }) => {
    const HeadingComponent = HEADING_COMPONENTS[titleSize] || H2;
    return (
        <HeadingComponent variant={variant}>{children}</HeadingComponent>
    );
};

DynamicHeading.propTypes = {
    titleSize: string,
    variant: string,
    children: string
};

const DynamicBody = ({ textSize, textBold, children }) => {
    const sizeMap = BODY_COMPONENTS[textSize] || BODY_COMPONENTS.regular;
    const BodyComponent = textBold ? sizeMap.bold : sizeMap.normal;
    return <BodyComponent>{children}</BodyComponent>;
};

DynamicBody.propTypes = {
    textSize: string,
    textBold: bool,
    children: string
};

const IconWithContent = ({
    icon,
    title,
    copy,
    linkText,
    linkUrl,
    linkDesktopUrl,
    contentStructured,
    listCheckMarks = false,
    subText = '',
    textSize = 'regular',
    iconPosition = 'top',
    textBold = false,
    iconSize = 'regular',
    titleSize = 'h2',
    headingVariant,
    textColor = 'dark',
    regularIconHorizontalMobileLayout = false,
    commonOptions
}) => {
    const structuredSubText =
        subText && stringToDast('paragraph', subText);

    const ContentSection = isHorizontalLayout(
        iconSize,
        regularIconHorizontalMobileLayout
    )
        ? HorizontalContentSection
        : VerticalContentSection;

    const isClickable = linkUrl && !linkText;
    const ContentSectionWrapper = isClickable
        ? ClickableLink
        : React.Fragment;
    const wrapperProps = isClickable ? { href: linkUrl } : {};

    return (
        <Container>
            <ContentSection
                gap="medium"
                alignItems={
                    iconPosition === 'center' ? 'center' : 'flex-start'
                }
                iconPosition={iconPosition}
                iconSize={iconSize}
                regularIconHorizontalMobileLayout={
                    regularIconHorizontalMobileLayout
                }
                hasButton={!!linkText}>
                <ContentSectionWrapper {...wrapperProps}>
                    <IconWrapper
                        iconSize={iconSize}
                        iconPosition={iconPosition}>
                        <Img imageData={icon.icon} />
                    </IconWrapper>
                    <ContentWrapper
                        textSize={textSize}
                        textBold={textBold}
                        iconPosition={iconPosition}
                        iconSize={iconSize}
                        regularIconHorizontalMobileLayout={
                            regularIconHorizontalMobileLayout
                        }
                        textColor={textColor}
                        hasContentAfterTitle={
                            title &&
                            (copy ||
                                !isStructuredContentEmpty(
                                    contentStructured
                                ))
                        }>
                        {title && (
                            <DynamicHeading
                                titleSize={titleSize}
                                variant={
                                    headingVariant ||
                                    commonOptions?.titleVariant
                                }>
                                {title}
                            </DynamicHeading>
                        )}
                        {(copy ||
                            !isStructuredContentEmpty(
                                contentStructured
                            )) &&
                            (isStructuredContentEmpty(
                                contentStructured
                            ) ? (
                                <DynamicBody
                                    textSize={textSize}
                                    textBold={textBold}>
                                    {copy}
                                </DynamicBody>
                            ) : (
                                <ContentParserStructured
                                    structuredCopy={contentStructured}
                                    listCheckMarks={listCheckMarks}
                                    leftAlign
                                    mobileCenterAlignText={shouldCenterAlignText(
                                        iconSize,
                                        regularIconHorizontalMobileLayout
                                    )}
                                    textColor={textColor}
                                    bodyCopyVariant={getBodyCopyVariant(
                                        textSize
                                    )}
                                />
                            ))}
                        {subText && (
                            <ContentParserStructured
                                bodyCopyVariant="small"
                                structuredCopy={structuredSubText}
                                leftAlign
                                mobileCenterAlignText={shouldCenterAlignText(
                                    iconSize,
                                    regularIconHorizontalMobileLayout
                                )}
                                textColor={textColor}
                            />
                        )}
                    </ContentWrapper>
                </ContentSectionWrapper>
            </ContentSection>
            {linkText && (
                <ButtonSection>
                    <Button link={linkUrl} desktopLink={linkDesktopUrl}>
                        {linkText}
                    </Button>
                </ButtonSection>
            )}
        </Container>
    );
};

IconWithContent.propTypes = {
    icon: shape({
        icon: shape(imageProps)
    }),
    title: string,
    copy: string,
    contentStructured: object,
    listCheckMarks: bool,
    linkText: string,
    linkUrl: string,
    linkDesktopUrl: string,
    subText: string,
    textSize: string,
    iconPosition: string,
    textBold: bool,
    iconSize: string,
    titleSize: string,
    headingVariant: string,
    textColor: string,
    regularIconHorizontalMobileLayout: bool,
    ...commonOptionsPropType
};

export default IconWithContent;
